import type { MediaItem } from "../types/contexts";

/**
 * Get the media type from a URL or media object
 * @param url The media URL
 * @param mediaType Optional media type hint
 * @returns MediaItem type
 */
export function getMediaType(
  url: string,
  mediaType?: string,
): MediaItem["type"] {
  if (mediaType) {
    const type = mediaType.toLowerCase();
    switch (type) {
      case "video":
        return "video";
      case "image":
        return "image";
      case "pdf":
        return "pdf";
      case "website":
        return "website";
    }
  }

  // Fallback to URL-based detection
  const lowerUrl = url.toLowerCase();

  // Video file extensions
  if (/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/.exec(lowerUrl)) {
    return "video";
  }

  // Image file extensions
  if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/.exec(lowerUrl)) {
    return "image";
  }

  // PDF file extensions
  if (/\.pdf$/.exec(lowerUrl)) {
    return "pdf";
  }

  // Default to video for unknown types
  return "video";
}

/**
 * Get the appropriate URL for a media item based on its type
 * @param mediaItem The media item
 * @returns The appropriate URL for the media type
 */
export function getMediaUrl(mediaItem: MediaItem): string {
  switch (mediaItem.type) {
    case "video":
      return mediaItem.url;
    case "image":
      return mediaItem.url;
    case "pdf":
      return mediaItem.url;
    case "website":
      return mediaItem.url;
    default:
      return mediaItem.url;
  }
}

/**
 * Get the thumbnail URL for a media item
 * @param mediaItem The media item
 * @returns The thumbnail URL
 */
export function getMediaThumbnail(mediaItem: MediaItem): string {
  // If thumbnail is provided, use it
  if (mediaItem.thumbnail.trim()) {
    return mediaItem.thumbnail;
  }

  // For videos, try to generate a thumbnail URL
  if (mediaItem.type === "video") {
    // This is a placeholder - in a real implementation, you might:
    // 1. Use a video thumbnail service
    // 2. Generate thumbnails on the server
    // 3. Use a default video thumbnail
    return mediaItem.thumbnail || "/default-video-thumbnail.jpg";
  }

  // For images, use the image URL as thumbnail
  if (mediaItem.type === "image") {
    return mediaItem.url;
  }

  // For other types, return empty string
  return "";
}

/**
 * Validate if a media item has the required properties
 * @param mediaItem The media item to validate
 * @returns True if the media item is valid
 */
export function isValidMediaItem(mediaItem: MediaItem): boolean {
  return !!(mediaItem.id && mediaItem.url.trim());
}

/**
 * Get a fallback media item when API data is not available
 * @param id The media item ID
 * @returns A fallback media item
 */
export function getFallbackMediaItem(id: string | number): MediaItem {
  return {
    id: typeof id === "string" ? parseInt(id, 10) : id,
    type: "video",
    url: "",
    title: "Loading...",
    description: "",
    thumbnail: "",
    products: [],
  };
}

/**
 * Format duration from seconds to MM:SS format
 * @param seconds Duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "00:00";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
}

/**
 * Check if a media item is a video
 * @param mediaItem The media item
 * @returns True if the media item is a video
 */
export function isVideo(mediaItem: MediaItem): boolean {
  return mediaItem.type === "video";
}

/**
 * Check if a media item is an image
 * @param mediaItem The media item
 * @returns True if the media item is an image
 */
export function isImage(mediaItem: MediaItem): boolean {
  return mediaItem.type === "image";
}

/**
 * Check if a media item is a PDF
 * @param mediaItem The media item
 * @returns True if the media item is a PDF
 */
export function isPDF(mediaItem: MediaItem): boolean {
  return mediaItem.type === "pdf";
}

/**
 * Check if a media item is a website
 * @param mediaItem The media item
 * @returns True if the media item is a website
 */
export function isWebsite(mediaItem: MediaItem): boolean {
  return mediaItem.type === "website";
}
